import React, { useState, useEffect } from "react";
import UsersList from "./UsersList";
import UserProfile from "../shared/UserProfile";
import Assessment from "../candidate/Assessment";
import Results from "../candidate/Results";
import EmployerDashboard from "../employer/EmployerDashboard";
import Matches from "../candidate/Matches";
import DashboardContainer from "./DashboardContainer";
import SimilarityValidator from "./SimilarityValidator";
import DiscountCodeManager from "./DiscountCodeManager";
import { api } from "../shared/axiosInstance";

const SuperUserDashboard = () => {
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [selectedUserRole, setSelectedUserRole] = useState("");
    const [activeTab, setActiveTab] = useState("profile"); // Default tab inside Users
    const [isSuperUser, setIsSuperUser] = useState(false);
    const [allQuestionsAnswered, setAllQuestionsAnswered] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [dashboardView, setDashboardView] = useState("users"); // "users" or "stats"
    const [showMfaResetConfirm, setShowMfaResetConfirm] = useState(false);
    const [mfaResetLoading, setMfaResetLoading] = useState(false);
    const [mfaResetMessage, setMfaResetMessage] = useState('');
    const [selectedUserEmail, setSelectedUserEmail] = useState('');
    const [selectedUserMfaEnabled, setSelectedUserMfaEnabled] = useState(false);

    useEffect(() => {
        const superUserStatus = localStorage.getItem("isSuperUser") === "true";
        setIsSuperUser(superUserStatus);
    }, []);

    useEffect(() => {
        if (!selectedUserId) return;
        const fetchUserRole = async () => {
            try {
                const response = await api.get(`/auth/user/${selectedUserId}`);
                setAllQuestionsAnswered(response.data.allQuestionsAnswered || false);
                setSelectedUserRole(response.data.role);
                setSelectedCompany(response.data.company || null);
                setSelectedUserEmail(response.data.email || '');
                setSelectedUserMfaEnabled(response.data.isMfaEnabled || false);
            } catch (err) {
                console.error("❌ Error fetching user role:", err);
            }
        };
        fetchUserRole();
    }, [selectedUserId]);

    const handleMfaReset = async () => {
        setMfaResetLoading(true);
        setMfaResetMessage('');
        try {
            await api.post(`/admin/users/${selectedUserId}/reset-mfa`);
            setMfaResetMessage('MFA reset successfully. User will be prompted to re-enroll on next login.');
            setShowMfaResetConfirm(false);
            setSelectedUserMfaEnabled(false);
        } catch (err) {
            setMfaResetMessage(err.response?.data?.message || err.response?.data?.error || 'Error resetting MFA.');
        } finally {
            setMfaResetLoading(false);
        }
    };

    if (!isSuperUser) {
        return (
            <div className="flex items-center justify-center h-screen">
                <p className="text-red-500 text-lg font-semibold text-orange">
                    Access Denied: Only Super Users can access this page.
                </p>
            </div>
        );
    }

    return (
        <div className="min-h-screen mt-4 flex flex-col bg-darkblue text-lightgray">
            {/* Dashboard Toggle */}
            <div className="flex justify-center bg-bluegray p-4 shadow-md">
                <button
                    onClick={() => setDashboardView("users")}
                    className={`px-6 py-2 mx-2 rounded ${dashboardView === "users" ? "bg-orange text-white" : "text-lightgray hover:bg-deepblue"}`}
                >
                    Users
                </button>
                <button
                    onClick={() => setDashboardView("discounts")}
                    className={`px-6 py-2 mx-2 rounded ${dashboardView === "discounts" ? "bg-orange text-white" : "text-lightgray hover:bg-deepblue"}`}
                >
                    Discount Codes
                </button>
                <button
                    onClick={() => setDashboardView("stats")}
                    className={`px-6 py-2 mx-2 rounded ${dashboardView === "stats" ? "bg-orange text-white" : "text-lightgray hover:bg-deepblue"}`}
                >
                    Stats
                </button>

                <button
                    onClick={() => setDashboardView("validator")}
                    className={`px-6 py-2 mx-2 rounded ${dashboardView === "validator" ? "bg-orange text-white" : "text-lightgray hover:bg-deepblue"}`}
                >
                    Validator
                </button>
            </div>

            {/* Dashboard Views */}
            <div className="flex-1 p-6">
                {dashboardView === "users" && (
                    <>
                        <UsersList
                            onSelectUser={(userId) => {
                                setSelectedUserId(userId);
                                setActiveTab("profile");

                                // Fetch full user data and store it in localStorage
                                api.get(`/auth/user/${userId}`).then((res) => {
                                    localStorage.setItem("impersonatedUser", JSON.stringify(res.data));
                                    localStorage.setItem("impersonatedUserId", userId);
                                    window.dispatchEvent(new Event("storage"));
                                });
                            }}
                        />

                        {selectedUserId && (
                            <div className="mt-6">
                                <div className="flex space-x-4 bg-bluegray p-2 rounded-lg shadow-md mb-4">
                                    <button
                                        onClick={() => setActiveTab("profile")}
                                        className={`px-4 py-2 rounded ${activeTab === "profile" ? "bg-orange text-white" : "text-lightgray hover:bg-deepblue"}`}
                                    >
                                        Profile
                                    </button>
                                    <button
                                        onClick={() => setActiveTab("assessment")}
                                        className={`px-4 py-2 rounded ${activeTab === "assessment" ? "bg-orange text-white" : "text-lightgray hover:bg-deepblue"}`}
                                    >
                                        Assessment
                                    </button>
                                    <button
                                        onClick={() => setActiveTab("matches")}
                                        className={`px-4 py-2 rounded ${activeTab === "matches" ? "bg-orange text-white" : "text-lightgray hover:bg-deepblue"}`}
                                    >
                                        Matches
                                    </button>
                                    {allQuestionsAnswered && (
                                        <button
                                            onClick={() => setActiveTab("results")}
                                            className={`px-4 py-2 rounded ${activeTab === "results" ? "bg-orange text-white" : "text-lightgray hover:bg-deepblue"}`}
                                        >
                                            Results
                                        </button>
                                    )}
                                    {selectedUserRole !== "candidate" && selectedCompany && (
                                        <button
                                            onClick={() => setActiveTab("employer")}
                                            className={`px-4 py-2 rounded ${activeTab === "employer" ? "bg-orange text-white" : "text-lightgray hover:bg-deepblue"}`}
                                        >
                                            Employer Dashboard
                                        </button>
                                    )}
                                </div>
                                {activeTab === "profile" && (
                                    <>
                                        <UserProfile key={selectedUserId} userId={selectedUserId} />

                                        {/* Admin MFA Reset Panel */}
                                        {selectedUserMfaEnabled && (
                                            <div className="mt-4 p-4 bg-bluegray rounded-lg shadow-lg">
                                                <h3 className="text-lg font-bold text-lightgray mb-2">Admin: MFA Management</h3>
                                                <p className="text-sm text-lightgray mb-3">
                                                    Reset this user's MFA configuration if they are locked out of their account.
                                                </p>

                                                {!showMfaResetConfirm ? (
                                                    <button
                                                        onClick={() => { setShowMfaResetConfirm(true); setMfaResetMessage(''); }}
                                                        className="py-2 px-4 bg-red text-white font-bold rounded hover:bg-red-700 transition"
                                                    >
                                                        Reset MFA
                                                    </button>
                                                ) : (
                                                    <div className="bg-darkblue p-4 rounded border border-red">
                                                        <p className="text-lightgray text-sm mb-2">
                                                            Reset MFA for <span className="text-orange font-bold">{selectedUserEmail}</span>?
                                                        </p>
                                                        <p className="text-red-400 text-xs mb-3">
                                                            This will remove their MFA configuration and require them to re-enroll on next login.
                                                        </p>
                                                        <div className="flex gap-3">
                                                            <button
                                                                onClick={handleMfaReset}
                                                                disabled={mfaResetLoading}
                                                                className="py-2 px-4 bg-red text-white font-bold rounded hover:bg-red-700 disabled:opacity-50"
                                                            >
                                                                {mfaResetLoading ? 'Resetting...' : 'Yes, Reset MFA'}
                                                            </button>
                                                            <button
                                                                onClick={() => setShowMfaResetConfirm(false)}
                                                                className="py-2 px-4 bg-bluegray text-lightgray font-bold rounded border border-lightblue hover:bg-deepblue"
                                                            >
                                                                Keep MFA
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}

                                                {mfaResetMessage && (
                                                    <p className={`mt-2 text-sm ${mfaResetMessage.includes('successfully') ? 'text-green-400' : 'text-red-400'}`}>
                                                        {mfaResetMessage}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}
                                {activeTab === "assessment" && <Assessment key={selectedUserId} userId={selectedUserId} />}
                                {activeTab === "matches" && <Matches key={selectedUserId} userId={selectedUserId} />}
                                {activeTab === "results" && allQuestionsAnswered && <Results key={selectedUserId} userId={selectedUserId} />}
                                {activeTab === "employer" && selectedUserRole !== "candidate" && selectedCompany && (
                                    <EmployerDashboard key={selectedUserId} userId={selectedUserId} />
                                )}
                            </div>
                        )}
                    </>
                )}
                {dashboardView === "stats" && <DashboardContainer />}
                {dashboardView === "validator" && <SimilarityValidator />}
                {dashboardView === "discounts" && <DiscountCodeManager />}
            </div>
        </div>
    );
};

export default SuperUserDashboard;
